import { useState } from 'react';
import { Button } from '@magicbrief/ui/src/components/button';
import {
  SelectButton,
  SelectContent,
} from '@magicbrief/ui/src/components/select';
import { ChevronDown } from '@magicbrief/ui/src/icons/chevron-down';
import { Spinner } from '@magicbrief/ui/src/components/spinner';
import { inferProcedureOutput } from '@trpc/server';
import { AppRouter } from '@magicbrief/server/src/trpc/router';
import { toast } from 'react-toastify';
import {
  Select,
  SelectItem,
  SelectValue,
} from 'src/components/AriaSelect/Select';
import { trpc } from 'src/lib/trpc';
import Alert from 'src/components/AlertV2';
import {
  HorizontalTabbedRadio,
  HorizontalTabbedRadioGroup,
} from 'src/components/HorizontalTabbedRadio/HorizontalTabbedRadio';
import AriaModal from 'src/components/AriaModal/AriaModal';
import { Icon } from 'src/components/Icon';
import AlertCircle from 'src/assets/svgicons/line/alert-circle.svg';

export const InsightsAITagging: React.FC = () => {
  const accounts = trpc.insightsAccounts.getManyAdAccounts.useQuery();
  const [selectedAccount, setSelectedAccount] = useState<{
    uuid: string;
    name: string;
  } | null>(null);

  const selectedAccountAITaggingStatus =
    trpc.insightsTags.getAdAccountAITaggingStatus.useQuery(
      {
        accountUuid: selectedAccount?.uuid ?? '',
      },
      {
        enabled: !!selectedAccount,
      }
    );

  return (
    <div className="w-full space-y-3">
      <p className="text-sm text-text-secondary">
        Configure AI tagging settings for this organisation&apos;s accounts
      </p>

      <div className="space-y-1.5">
        <label
          className="text-sm font-semibold text-primary"
          htmlFor="account-select-ai-tagging"
        >
          Insights Ad Account
        </label>
        <Select
          onSelectionChange={(key) => {
            const match = accounts.data?.find(
              (x) => x.uuid === (key as string)
            );
            if (match) {
              setSelectedAccount({ uuid: key as string, name: match.name });
            }
          }}
          id="account-select-ai-tagging"
        >
          <SelectButton>
            <SelectValue className="text-ellipsis" />
            <ChevronDown className="size-4 text-primary/50" />
          </SelectButton>
          <SelectContent
            popoverClassName="w-[var(--trigger-width)]"
            items={accounts.data?.map((opt) => ({
              id: opt.uuid,
              name: opt.name,
              platform: opt.platform,
            }))}
          >
            {(item) => (
              <SelectItem
                className="flex flex-col items-start text-left"
                key={item.id}
                id={item.id}
              >
                <div>{item.name}</div>
                <div className="text-xxs capitalize text-text-secondary">
                  {item.platform}
                </div>
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {selectedAccountAITaggingStatus.isFetching && (
        <div className="flex w-full flex-col items-center gap-1.5 text-xs text-primary">
          <Spinner />
          <div>Retrieving AI tagging settings for account...</div>
        </div>
      )}

      {!selectedAccountAITaggingStatus.isFetching &&
        !!selectedAccountAITaggingStatus.error && (
          <Alert title="Error" variant="danger">
            {selectedAccountAITaggingStatus.error.message}
          </Alert>
        )}

      {!!selectedAccount &&
        !selectedAccountAITaggingStatus.isFetching &&
        !!selectedAccountAITaggingStatus.data && (
          <InsightsAITaggingForm
            accountUuid={selectedAccount.uuid}
            accountName={selectedAccount.name}
            status={selectedAccountAITaggingStatus.data}
          />
        )}
    </div>
  );
};

const InsightsAITaggingForm: React.FC<{
  accountUuid: string;
  accountName: string;
  status: inferProcedureOutput<
    AppRouter['insightsTags']['getAdAccountAITaggingStatus']
  >;
}> = ({ accountUuid, status, accountName }) => {
  const trpcUtils = trpc.useUtils();

  const defaultLevel = status.registered ? status.processLevel : null;
  const [selectedLevel, setSelectedLevel] = useState<
    'textual' | 'full-asset' | null
  >(defaultLevel);

  const defaultAdVolume = status.registered ? status.adVolume : null;
  const [selectedAdVolume, setSelectedAdVolume] = useState<
    'winners' | 'recent-mid-spend' | null
  >(defaultAdVolume);

  const defaultActivePaused = status.registered
    ? status.paused
      ? 'paused'
      : 'active'
    : 'active';

  const [selectedActivePaused, setSelectedActivePaused] = useState<
    'active' | 'paused'
  >(defaultActivePaused);

  const update = trpc.insightsTags.updateAdAccountAITaggingStatus.useMutation({
    async onSuccess() {
      await trpcUtils.insightsTags.getAdAccountAITaggingStatus.invalidate({
        accountUuid,
      });
      toast.success('AI tagging settings updated', {
        className: 'toast-success',
      });
    },
  });

  const [isResetHistoryModalOpen, setIsResetHistoryModalOpen] = useState(false);

  return (
    <>
      <div className="space-y-1.5">
        <label
          className="text-sm font-semibold text-primary"
          htmlFor="account-select-ai-tagging"
        >
          Account Information
        </label>
        <div className="grid border-collapse grid-flow-row grid-cols-2 rounded-md border border-solid border-purple-100 text-xs text-primary">
          <div className="col-span-1 border-r border-solid border-purple-100 p-2">
            Registered
          </div>
          <div className="col-span-1 p-2">
            {status.registered ? '✅' : '❌'}
          </div>
          {status.registered && (
            <>
              <div className="col-span-1 border-r border-t border-solid border-purple-100 p-2">
                Total Ad Count
              </div>
              <div className="col-span-1 border-t border-solid border-purple-100 p-2">
                {status.totalAdCount}
              </div>
              <div className="col-span-1 border-r border-t border-solid border-purple-100 p-2">
                Total Processed Ad Count
              </div>
              <div className="col-span-1 border-t border-solid border-purple-100 p-2">
                {status.totalProcessedAdCount}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <label
          className="text-sm font-semibold text-primary"
          htmlFor="account-select-ai-tagging"
        >
          Tagging Level
        </label>
        <Select
          onSelectionChange={(key) =>
            setSelectedLevel(key as typeof selectedLevel)
          }
          placeholder="Unregistered"
          defaultSelectedKey={
            status.registered ? status.processLevel : undefined
          }
          id="account-select-ai-tagging"
        >
          <SelectButton>
            <SelectValue className="text-ellipsis" />
            <ChevronDown className="size-4 text-primary/50" />
          </SelectButton>
          <SelectContent
            popoverClassName="w-[var(--trigger-width)]"
            items={[
              { id: 'textual', name: 'Textual' },
              { id: 'full-asset', name: 'Full Asset' },
            ]}
          >
            {(item) => (
              <SelectItem key={item.id} id={item.id}>
                {item.name}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <label
          className="text-sm font-semibold text-primary"
          htmlFor="account-select-ai-ad-volume"
        >
          Ad Volume
        </label>
        <Select
          onSelectionChange={(key) =>
            setSelectedAdVolume(key as typeof selectedAdVolume)
          }
          placeholder="Unregistered"
          defaultSelectedKey={status.registered ? status.adVolume : undefined}
          id="account-select-ai-ad-volume"
        >
          <SelectButton>
            <SelectValue className="text-ellipsis" />
            <ChevronDown className="size-4 text-primary/50" />
          </SelectButton>
          <SelectContent
            popoverClassName="w-[var(--trigger-width)]"
            items={[
              { id: 'recent-mid-spend', name: 'Recent Mid Spend' },
              { id: 'winners', name: 'Winners' },
            ]}
          >
            {(item) => (
              <SelectItem key={item.id} id={item.id}>
                {item.name}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <label
          className="text-sm font-semibold text-primary"
          htmlFor="active-paused-ai-tagging-radio"
        >
          Active / Paused
        </label>
        <HorizontalTabbedRadioGroup
          defaultValue={
            status.registered ? (status.paused ? 'paused' : 'active') : 'active'
          }
          value={selectedActivePaused}
          onChange={(s) =>
            setSelectedActivePaused(s as typeof selectedActivePaused)
          }
          id="active-paused-ai-tagging-radio"
          groupAriaLabel="Active or paused AI tagging"
          sizing="evenly"
        >
          <HorizontalTabbedRadio value="active">Active</HorizontalTabbedRadio>
          <HorizontalTabbedRadio value="paused">Paused</HorizontalTabbedRadio>
        </HorizontalTabbedRadioGroup>
      </div>

      <div className="flex gap-2">
        <Button
          className="basis-1/2"
          variant="destructive"
          onPress={() => setIsResetHistoryModalOpen(true)}
        >
          Reset AI Tag History
        </Button>

        {isResetHistoryModalOpen && (
          <ResetAITaggingHistoryModal
            accountName={accountName}
            accountUuid={accountUuid}
            onClose={() => setIsResetHistoryModalOpen(false)}
          />
        )}

        <Button
          loading={update.isLoading}
          className="basis-1/2"
          isDisabled={
            !selectedLevel ||
            (defaultLevel === selectedLevel &&
              defaultActivePaused === selectedActivePaused &&
              defaultAdVolume === selectedAdVolume)
          }
          onPress={() => {
            if (selectedLevel || selectedAdVolume) {
              update.mutate({
                accountUuid,
                processLevel: selectedLevel ?? undefined,
                adVolume: selectedAdVolume ?? undefined,
                pause: selectedActivePaused === 'paused' ? true : false,
              });
            }
          }}
        >
          Update
        </Button>
      </div>
    </>
  );
};

interface ResetAITaggingHistoryModalProps {
  accountName: string;
  accountUuid: string;
  onClose: () => void;
}

export const ResetAITaggingHistoryModal: React.FC<
  ResetAITaggingHistoryModalProps
> = ({ accountName, accountUuid, onClose }) => {
  const [isOpen, setIsOpen] = useState(true);

  const trpcUtils = trpc.useUtils();

  const reset = trpc.insightsTags.resetAdAccountAITaggingHistory.useMutation({
    onSuccess: async () => {
      await trpcUtils.insightsTags.getMagicAIIngestionTagInfo.invalidate();
      setIsOpen(false);
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : 'Unknown error');
    },
  });

  return (
    <AriaModal
      footer={
        <div className="flex flex-row justify-end gap-2.5">
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            loading={reset.isLoading}
            variant="destructive"
            onPress={() => {
              reset.mutate({
                accountUuid,
              });
            }}
          >
            Reset
          </Button>
        </div>
      }
      show={isOpen}
      onClose={() => setIsOpen(false)}
      afterClose={onClose}
      title={`Reset ${accountName}'s AI Tagging History?`}
    >
      <div className="space-y-4">
        <div className="flex flex-row gap-3 rounded-lg bg-red-50 pb-3 pl-3 pr-6 pt-2.5 text-xs text-red-700">
          <Icon className="size-4 shrink-0">
            <AlertCircle />
          </Icon>
          <div>
            Resetting this will re-expose the AI tagging setup interface for all
            tags the account has previously handled! This does will not delete
            any tags themselves.
          </div>
        </div>
      </div>
    </AriaModal>
  );
};
